// Optional, replayable onboarding tour for first-time visitors.
//
// Drives attention through a deterministic sequence of UI features:
//   1. The brand strip
//   2. The "X active" badge (jump-to-now)
//   3. Live stats grid (events / counts / countries)
//   4. Filters row (category / secondary / status / confidence)
//   5. Events list
//   6. Timeline scrubber + presets
//   7. (Desktop only) Marker / heatmap view toggle
//
// Targets are discovered by `data-tour="<key>"` attributes on the host
// components — that decoupling means the tour script lives in one place
// instead of being wired through component props.
//
// On desktop: full-page dimmed backdrop with a transparent rectangular
// cutout over the target element, tooltip card positioned in the most
// available quadrant.
//
// On mobile (<sm): the sidebar is force-expanded so its inner targets
// are reachable, the cutout is replaced with a soft outline ring (the
// tooltip stays as a fixed bottom-sheet so it never collides with the
// element).
//
// Persistence: completion stored under `<domain>:tour:v1`. The
// tour will not auto-start if (a) the user has already completed, (b)
// the URL has a selected event id (so direct event links aren't
// hijacked), or (c) the legal gate hasn't been accepted yet. Replay is
// always possible via the `startTour()` exported helper.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { config } from '../lib/config';

const TOUR_KEY = `${config.app.domain}:tour:v1`;
const LEGAL_KEY = `${config.app.domain}:legal-ack:v1`;

interface Step {
  key: string;
  title: string;
  body: string;
  desktopOnly?: boolean;
  /** When true, the spotlight is suppressed and only the tooltip shows.
   *  Useful for the welcome and farewell steps. */
  global?: boolean;
}

const STEPS: Step[] = [
  {
    key: 'welcome',
    title: `Welcome to ${config.app.domain}`,
    body: 'A 60-second tour of the map and filters. You can skip any time.',
    global: true,
  },
  {
    key: 'brand',
    title: 'Your control panel',
    body: 'Brand on the left; everything you need to navigate the data lives in this one panel — stats, filters, events.',
  },
  {
    key: 'active-badge',
    title: 'Jump to active events',
    body: 'Click the red "X active" badge any time to clear filters and zoom to events happening right now.',
  },
  {
    key: 'stats',
    title: 'Live stats',
    body: 'Events, counts, and deaths update in real time as you filter — single source of truth, always honest about scope.',
  },
  {
    key: 'filters',
    title: 'Filters',
    body: 'Narrow by category, secondary, status, or confidence. Try selecting a single category.',
  },
  {
    key: 'events-list',
    title: 'Events list',
    body: 'Every visible event. Click any row to open its detail panel with full source citations.',
  },
  {
    key: 'timeline',
    title: 'Timeline scrubber',
    body: 'Drag the handles or click a preset (All time, 1 year, 6 months, 1 month, 7 days) to rescale the date range. Stats update with the selection.',
  },
  {
    key: 'view-toggle',
    title: 'Markers vs heatmap',
    body: 'Switch between discrete event markers and a case-weighted heatmap. Heatmap is best for spotting clusters at low zoom.',
    desktopOnly: true,
  },
  {
    key: 'farewell',
    title: 'You\'re ready',
    body: 'Educational use only — every numeric claim is source-cited. Replay this tour anytime via the "Take the tour" link at the bottom of the sidebar.',
    global: true,
  },
];

// ---------------------------------------------------------------------------
// External trigger — sidebar footer calls this to replay.
// ---------------------------------------------------------------------------

export function startTour() {
  try { window.localStorage.removeItem(TOUR_KEY); } catch { /* ignore */ }
  // Notify the mounted tour to re-arm.
  window.dispatchEvent(new CustomEvent('app:tour:start'));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardingTour() {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const isMobileRef = useRef(false);

  const isMobile = useCallback(() => window.innerWidth < 640, []);
  const isDesktopOnlyStep = STEPS[stepIdx]?.desktopOnly && isMobileRef.current;

  // Decide whether to auto-start at mount, and listen for replay events.
  useEffect(() => {
    let didStart = false;
    const startIfEligible = () => {
      if (didStart) return;
      try {
        const tourDone = window.localStorage.getItem(TOUR_KEY);
        const legalAck = window.localStorage.getItem(LEGAL_KEY);
        // Only auto-start once both: legal accepted + tour not yet completed.
        // Skip if a specific event is being deep-linked, so direct citation
        // URLs work without onboarding flicker.
        const params = new URLSearchParams(window.location.search);
        const directEvent = !!params.get('id');
        if (!tourDone && legalAck && !directEvent) {
          didStart = true;
          // Defer one frame so target elements are mounted.
          requestAnimationFrame(() => {
            setStepIdx(0);
            setActive(true);
          });
        }
      } catch { /* localStorage blocked — never auto-start */ }
    };
    // Initial check.
    startIfEligible();
    // Re-check periodically while legal gate is dismissed (rare, but cheap).
    const t = window.setInterval(startIfEligible, 1500);
    // Manual replay handler.
    const onReplay = () => {
      setStepIdx(0);
      setActive(true);
    };
    window.addEventListener('app:tour:start', onReplay);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('app:tour:start', onReplay);
    };
  }, []);

  // Track mobile state for desktop-only step skipping.
  useEffect(() => {
    const update = () => { isMobileRef.current = isMobile(); };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isMobile]);

  // When entering a sidebar-targeted step on mobile, auto-expand the sidebar
  // so its inner targets exist in the DOM and are visible.
  useEffect(() => {
    if (!active || !isMobileRef.current) return;
    const step = STEPS[stepIdx];
    if (!step || step.global) return;
    const insideSidebar = ['brand', 'active-badge', 'stats', 'filters', 'events-list'].includes(step.key);
    if (!insideSidebar) return;
    const toggle = document.querySelector<HTMLButtonElement>('aside button[aria-expanded="false"]');
    if (toggle) toggle.click();
  }, [active, stepIdx]);

  // Auto-skip desktop-only steps on mobile.
  useEffect(() => {
    if (!active) return;
    if (isDesktopOnlyStep) {
      const dir = stepIdx >= STEPS.length - 1 ? -1 : 1;
      setStepIdx(idx => Math.max(0, Math.min(STEPS.length - 1, idx + dir)));
    }
  }, [active, stepIdx, isDesktopOnlyStep]);

  // Resolve the target rect whenever the step or window size changes.
  useLayoutEffect(() => {
    if (!active) { setRect(null); return; }
    const step = STEPS[stepIdx];
    if (!step || step.global) { setRect(null); return; }

    const recompute = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${step.key}"]`);
      if (!el) { setRect(null); return; }
      // Ensure visibility before measuring.
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        setRect(r);
      });
    };

    recompute();
    const onResize = () => recompute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [active, stepIdx]);

  const finish = useCallback(() => {
    try { window.localStorage.setItem(TOUR_KEY, new Date().toISOString()); } catch { /* */ }
    setActive(false);
    setStepIdx(0);
  }, []);

  if (!active) return null;
  const step = STEPS[stepIdx];
  if (!step) return null;
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <>
      {/* Backdrop with optional cutout. Always visible to dim non-target areas. */}
      <Backdrop rect={rect} mobile={isMobileRef.current} />
      {/* Soft outline ring on the target — visible on both desktop and mobile. */}
      {rect && <Spotlight rect={rect} />}
      {/* Tooltip card */}
      <Tooltip
        rect={rect}
        step={step}
        stepIdx={stepIdx}
        total={STEPS.length}
        isFirst={isFirst}
        isLast={isLast}
        onPrev={() => setStepIdx(i => Math.max(0, i - 1))}
        onNext={() => isLast ? finish() : setStepIdx(i => Math.min(STEPS.length - 1, i + 1))}
        onSkip={finish}
        mobile={isMobileRef.current}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Backdrop({ rect, mobile }: { rect: DOMRect | null; mobile: boolean }) {
  // On mobile, never carve a hole — the sidebar covers most of the screen
  // when expanded, so we use a flat dim instead. On desktop, an SVG mask
  // gives a crisp rectangular cutout around the target.
  if (mobile || !rect) {
    return (
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[80] bg-black/55 transition-opacity duration-300"
      />
    );
  }
  // Pad the cutout so the target has breathing room.
  const pad = 8;
  const x = Math.max(0, rect.left - pad);
  const y = Math.max(0, rect.top - pad);
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;
  return (
    <svg
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[80] h-full w-full"
      style={{ width: '100vw', height: '100vh' }}
    >
      <defs>
        <mask id="tour-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={12} ry={12} fill="black" />
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill="rgba(0, 0, 0, 0.55)"
        mask="url(#tour-mask)"
      />
    </svg>
  );
}

function Spotlight({ rect }: { rect: DOMRect }) {
  const pad = 8;
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed z-[81] rounded-xl ring-2 ring-[var(--color-accent)] transition-all duration-300"
      style={{
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
        boxShadow: '0 0 0 4px rgba(82, 71, 229, 0.18)',
      }}
    />
  );
}

interface TooltipProps {
  rect: DOMRect | null;
  step: Step;
  stepIdx: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
  mobile: boolean;
}

function Tooltip(props: TooltipProps) {
  const { rect, step, stepIdx, total, isFirst, isLast, onPrev, onNext, onSkip, mobile } = props;

  // Mobile: fixed bottom sheet, never positional.
  // Desktop: positioned next to the spotlight, fallback to centered.
  const cardStyle = mobile
    ? { left: 12, right: 12, bottom: 12, maxWidth: 'unset' }
    : computeDesktopPosition(rect);

  return (
    <div
      role="dialog"
      aria-label={step.title}
      className="fixed z-[90] w-[min(420px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl"
      style={{
        ...cardStyle,
        boxShadow:
          '0 0 0 1px rgba(0,0,0,0.08), 0 24px 48px -8px rgba(0,0,0,0.45), 0 8px 16px -4px rgba(0,0,0,0.25)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-line)] bg-[var(--color-surface-2)] px-4 py-2.5">
        <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-3)]">
          Step {stepIdx + 1} of {total}
        </span>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Skip tour"
          className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-3)] hover:bg-white hover:text-[var(--color-text)]"
        >
          <X size={14} strokeWidth={2.25} />
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <h3 className="text-[15px] font-semibold leading-snug tracking-tight text-[var(--color-text)]">
          {step.title}
        </h3>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-[var(--color-text-2)]">
          {step.body}
        </p>
      </div>

      {/* Footer controls */}
      <div className="flex items-center justify-between gap-3 border-t border-[var(--color-line)] px-4 py-3">
        <button
          type="button"
          onClick={onSkip}
          className="text-[12px] font-medium text-[var(--color-text-3)] hover:text-[var(--color-accent)]"
        >
          Skip tour
        </button>
        <div className="flex items-center gap-1.5">
          {!isFirst && (
            <button
              type="button"
              onClick={onPrev}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-[var(--color-surface-2)] px-3 text-[12.5px] font-medium text-[var(--color-text-2)] ring-1 ring-[var(--color-line-strong)] hover:bg-white hover:text-[var(--color-text)]"
            >
              <ChevronLeft size={13} strokeWidth={2.25} />
              Back
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            autoFocus
            className="inline-flex h-8 items-center gap-1 rounded-md bg-[var(--color-accent)] px-3 text-[12.5px] font-semibold text-white shadow-[0_1px_2px_rgba(82,71,229,0.25)] hover:bg-[var(--color-accent)]/90"
          >
            {isLast ? 'Got it' : 'Next'}
            {!isLast && <ChevronRight size={13} strokeWidth={2.25} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function computeDesktopPosition(rect: DOMRect | null): React.CSSProperties {
  // Centered fallback for global / unresolved targets.
  if (!rect) {
    return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  }
  const margin = 16;
  const cardW = 420;
  const cardH = 200;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Try right of element first; fall back to below; then left; then above.
  const spaceRight = vw - rect.right;
  const spaceBelow = vh - rect.bottom;
  const spaceLeft = rect.left;
  const spaceAbove = rect.top;

  if (spaceRight >= cardW + margin) {
    return {
      left: rect.right + margin,
      top: clamp(rect.top, margin, vh - cardH - margin),
    };
  }
  if (spaceBelow >= cardH + margin) {
    return {
      top: rect.bottom + margin,
      left: clamp(rect.left, margin, vw - cardW - margin),
    };
  }
  if (spaceLeft >= cardW + margin) {
    return {
      left: rect.left - cardW - margin,
      top: clamp(rect.top, margin, vh - cardH - margin),
    };
  }
  if (spaceAbove >= cardH + margin) {
    return {
      top: rect.top - cardH - margin,
      left: clamp(rect.left, margin, vw - cardW - margin),
    };
  }
  // No space anywhere — center it.
  return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
